import React, { useState, useRef, useEffect } from "react";
import { faExchangeAlt, faPenSquare } from '@fortawesome/free-solid-svg-icons';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./AIGeneratorForm.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlaneDeparture, faPlaneArrival, faCalendar, faUsers, faBed, faUtensils, faChevronDown, faChevronUp, faPlus, faMinus, faExclamationCircle, faCheckCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { RiCalendarLine } from "react-icons/ri"; // Make sure to import RiCalendarLine
import airports from "../../Data/AirportList.json";
import moment from "moment";
import { useAuth } from "../../Common/Configurations/AuthContext";
import packageDestinationsData from "../../Data/PackageDestinations.json";
import GeneratingAnimation from "./GeneratingAnimation";

// --- Components (CityCard, etc.) remain unchanged ---
const cityImageMap = {
    'Default': 'https://plus.unsplash.com/premium_photo-1680497811614-4f93025d7e57?q=80&w=1965&auto=format&fit=crop&ixlib-rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
};

// --- ADDED THIS COMPONENT ---
const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
    <div className="input-group">
        <input type="text" className="form-control" value={value} onClick={onClick} ref={ref} readOnly placeholder="Select date"/>
        <button className="btn btn-secondary" type="button" onClick={onClick}><RiCalendarLine /></button>
    </div>
));

const CityCard = ({ city, onRemove, onNightsChange, canAddNight }) => {
    const imageUrl = cityImageMap[city.name] || cityImageMap['Default'];
    return (
        <div className="generate-city-card">
            <img src={imageUrl} alt={city.name} className="generate-card-image" />
            <div className="generate-card-content">
                <div className="generate-card-header">
                    <span className="generate-city-name">{city.name}</span>
                    <button type="button" className="generate-remove-card-btn" onClick={onRemove}>&times;</button>
                </div>
                <div className="generate-night-controls">
                    <button type="button" onClick={() => onNightsChange(-1)} disabled={city.nights <= 1}>-</button>
                    <span>{city.nights} {city.nights > 1 ? 'Nights' : 'Night'}</span>
                    <button type="button" onClick={() => onNightsChange(1)} disabled={!canAddNight}>+</button>
                </div>
            </div>
        </div>
    );
};

const SuggestedCities = ({ country, onAddCity, remainingNights, itineraryCities }) => {
    const countryData = packageDestinationsData.find(d => d.Destination === country);
    const suggestions = countryData?.Places?.slice(0, 10) || [];
    
    const addedCityNames = new Set(itineraryCities.map(city => city.name));

    if (!suggestions.length) return null;
    return (
        <div className="generate-suggestions-container">
            <h3 className="generate-suggestions-title">Popular cities in {country} to get you started</h3>
            <div className="generate-suggestions-carousel">
                {suggestions.map((cityName, index) => {
                    const isAdded = addedCityNames.has(cityName);
                    return (
                        <div key={index} className="generate-suggestion-card">
                            <div className="generate-suggestion-content">
                                <h4>{cityName}</h4>
                                {isAdded ? (
                                    <button type="button" className="added-btn" disabled>✓ Added</button>
                                ) : (
                                    <button type="button" onClick={() => onAddCity(cityName)} disabled={remainingNights <= 0}>+ Add</button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};


const Modal = ({ isOpen, onClose, children, customClassName = '' }) => {
    if (!isOpen) return null;
    return (
        <div className={`custom-modal-backdrop ${customClassName}-backdrop`} onClick={onClose}>
            <div className={`modal-content ${customClassName}`} onClick={e => e.stopPropagation()}>
                <button className="modal-close-btn" onClick={onClose}>&times;</button>
                {children}
            </div>
        </div>
    );
};

const getCleanCityName = (airport) => {
    if (!airport || !airport.cityName) return '';
    return airport.cityName.split(',')[0];
};

const calculateTotalNights = (start, end) => {
    if (start && end) {
        const totalNights = moment(end).diff(moment(start), 'days');
        return totalNights > 0 ? totalNights : 0;
    }
    return 0;
};

const defaultStartDate = moment().add(1, 'days').toDate();
const defaultEndDate = moment(defaultStartDate).add(3, 'days').toDate();

const SupplierAIGeneratorForm = ({ onGenerate, isGenerating, onSwitchToFlight, onSwitchToLand, onSwitchToManual }) => {
    const [formData, setFormData] = useState({
        departureCity: null,
        arrivalCity: null,
        country: null,
        startDate: defaultStartDate,
        endDate: defaultEndDate,
        numAdults: 1,
        numChildren: 0,
        numInfants: 0,
        numRooms: 1,
        hotelCategory: '4 Star',
        mealType: 'MAP',
        cabinClass: 'Economy',
        isDirectFLight: false,
        isDomestic: "DOMESTIC",
        journeyType: "RoundTrip",
        preferred: "REGULAR"
    });

    // Added this state to manage duration
    const [numberOfNights, setNumberOfNights] = useState(calculateTotalNights(defaultStartDate, defaultEndDate));

    const { user } = useAuth();
    const [departureResults, setDepartureResults] = useState([]);
    const [arrivalResults, setArrivalResults] = useState([]);
    const [displayFrom, setDisplayFrom] = useState('');
    const [displayTo, setDisplayTo] = useState('');
    const [cities, setCities] = useState([]);
    const [citySuggestions, setCitySuggestions] = useState([]);
    const [isPassengerDropdownOpen, setPassengerDropdownOpen] = useState(false);
    const passengerRef = useRef(null);
    const [isCityModalOpen, setCityModalOpen] = useState(false);
    const [newCityName, setNewCityName] = useState('');
    const [newCityNights, setNewCityNights] = useState('');
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
    const [errors, setErrors] = useState({});

    const totalTripNights = calculateTotalNights(formData.startDate, formData.endDate);
    const allocatedNights = cities.reduce((total, city) => total + parseInt(city.nights || 0), 0);
    const areNightsBalanced = totalTripNights > 0 && totalTripNights === allocatedNights;
    const remainingNights = totalTripNights - allocatedNights;

    useEffect(() => {
        function handleClickOutside(event) {
            if (passengerRef.current && !passengerRef.current.contains(event.target)) {
                setPassengerDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [passengerRef]);

    const handleAirportSearch = (e, type) => {
        const value = e.target.value;
        if (type === 'departure') setDisplayFrom(value);
        else setDisplayTo(value);

        if (value.length > 2) {
            const lowerValue = value.toLowerCase();
            const filtered = airports.filter(airport =>
                airport.cityName.toLowerCase().includes(lowerValue) || airport.airportCode.toLowerCase().includes(lowerValue)
            ).slice(0, 7);
            if (type === 'departure') setDepartureResults(filtered);
            else setArrivalResults(filtered);
        } else {
            if (type === 'departure') setDepartureResults([]);
            else setArrivalResults([]);
        }
    };

    const handleDepartureSelect = (airport) => {
        setFormData(prev => ({ ...prev, departureCity: airport }));
        setDisplayFrom(`${getCleanCityName(airport)} (${airport.airportCode})`);
        setDepartureResults([]);
    };

    // --- UPDATED THIS FUNCTION ---
    const handleArrivalSelect = (airport) => {
        const cleanCityName = getCleanCityName(airport);
        const nights = calculateTotalNights(formData.startDate, formData.endDate);

        if (nights > 0) {
            setCities([{ name: cleanCityName, nights }]);
        } else {
            setCities([]);
        }
        
        setFormData(prev => ({ ...prev, arrivalCity: airport, country: airport.countryName }));
        setDisplayTo(`${cleanCityName} (${airport.airportCode})`);
        setArrivalResults([]);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;
        setFormData(prev => ({ ...prev, [name]: val }));
    };

    const handlePassengerChange = (type, delta) => {
        setFormData(prev => {
            const newValue = prev[type] + delta;
            if (type === 'numAdults' && newValue < 1) return prev;
            if (newValue < 0) return prev;
            return { ...prev, [type]: newValue };
        });
    };
    
    // --- NEW DATE HANDLERS ---
    const handleStartDateChange = (date) => {
        const newStartDate = date;
        const newEndDate = moment(newStartDate).add(numberOfNights, 'days').toDate();
        setFormData(prev => ({ ...prev, startDate: newStartDate, endDate: newEndDate }));

        if (cities.length === 1) {
            const newTotalNights = calculateTotalNights(newStartDate, newEndDate);
            setCities([{ name: cities[0].name, nights: newTotalNights > 0 ? newTotalNights : 1 }]);
        }
    };

    const handleEndDateChange = (date) => {
        const newEndDate = date;
        const newStartDate = formData.startDate;
        setFormData(prev => ({...prev, endDate: newEndDate}));

        const newTotalNights = calculateTotalNights(newStartDate, newEndDate);
        if (newTotalNights >= 1) {
            setNumberOfNights(newTotalNights);
        }

        if (cities.length === 1) {
            setCities([{ name: cities[0].name, nights: newTotalNights > 0 ? newTotalNights : 1 }]);
        }
    };
    
    // The rest of the functions (removeCity, etc.) remain unchanged...
    const addCityFromSuggestion = (cityName) => { if (remainingNights <= 0 || cities.some(c => c.name === cityName)) { return; } setCities([...cities, { name: cityName, nights: 1 }]); };
    const removeCity = (indexToRemove) => { setCities(cities.filter((_, index) => index !== indexToRemove)); };
    const handleNightsChange = (index, delta) => { const newCities = [...cities]; const newNights = newCities[index].nights + delta; if (newNights < 1) return; if (delta > 0 && remainingNights <= 0) return; newCities[index] = { ...newCities[index], nights: newNights }; setCities(newCities); };
      const handleCitySearch = (e) => { 
        const value = e.target.value; 
        setNewCityName(value); 
        if (value.length > 1 && formData.country) { 
            const lowerValue = value.toLowerCase(); 
            const countryData = packageDestinationsData.find(d => d.Destination === formData.country);
            const filteredCities = countryData?.Places?.filter(city => city.toLowerCase().includes(lowerValue)) || [];
            setCitySuggestions(filteredCities.slice(0, 7)); 
        } else { 
            setCitySuggestions([]); 
        } 
    };
    const handleCitySelect = (city) => { setNewCityName(city); setCitySuggestions([]); };
    const handleAddCity = () => { const nightsToAdd = parseInt(newCityNights, 10); if (newCityName.trim() && nightsToAdd > 0) { if (allocatedNights + nightsToAdd > totalTripNights) return; setCities([...cities, { name: newCityName.trim(), nights: nightsToAdd }]); setNewCityName(''); setNewCityNights(''); setCitySuggestions([]); setCityModalOpen(false); } };
    const passengerDisplayText = `${formData.numAdults} Adults, ${formData.numChildren} Kids, ${formData.numInfants} Infants`;
    const validate = () => { const newErrors = {}; let isValid = true; if (!formData.departureCity) { newErrors.departure = "Please select a departure city."; isValid = false; } if (!formData.arrivalCity) { newErrors.arrival = "Please select an arrival city."; isValid = false; } if (cities.length === 0) { newErrors.cities = "Please add at least one city to visit."; isValid = false; } if (!areNightsBalanced) { newErrors.nights = `The total nights allocated must match trip duration.`; isValid = false; } setErrors(newErrors); return isValid; }
    const handleGenerateClick = () => { if (!validate()) return; const payload = { packageType: "Flight", fromDestination: formData.departureCity.airportCode, toDestination: formData.arrivalCity.airportCode, destination: formData.arrivalCity.countryName, departureDate: moment(formData.startDate).format("YYYY-MM-DD"), returnDate: moment(formData.endDate).format("YYYY-MM-DD"), adults: formData.numAdults, children: formData.numChildren, infants: formData.numInfants, numberOfPeople: formData.numAdults + formData.numChildren + formData.numInfants, currency: user?.currency || "INR", numberOfNights: totalTripNights, placesToCover: cities.map(city => ({ name: city.name, nights: city.nights })), companyId: user?.cid, userId: user?.id, cabinClass: formData.cabinClass, isDirectFlight: formData.isDirectFLight, isDomestic: formData.isDomestic, journeyType: formData.journeyType, preferred: formData.preferred }; onGenerate(payload); };

    return (
        <>
            <div className="ai-generator-container">
                {!isGenerating && (
                <div className="ai-form-switch-actions">
                    <button type="button" className="switch-btn" onClick={onSwitchToFlight}><FontAwesomeIcon icon={faExchangeAlt} /> Switch to Flight Package</button>
                    <button type="button" className="switch-btn" onClick={onSwitchToLand}><FontAwesomeIcon icon={faExchangeAlt} /> Switch to Land Package</button>
                    <button type="button" className="switch-btn" onClick={onSwitchToManual}><FontAwesomeIcon icon={faPenSquare} /> Switch to Manual Form</button>
                </div>
                 )}
                <h2 className="ai-generator-title">Generate Supplier Package with AI</h2>
                <p className="ai-generator-subtitle">Enter supplier preferences to generate a detailed itinerary.</p>
                
                {isGenerating ? (<GeneratingAnimation />) : (
                    <form className="ai-form">
                        {/* --- UPDATED LAYOUT --- */}
                        <div className="row">
                            <div className="col-3">
                                <div className="form-group">
                                    <label><FontAwesomeIcon icon={faPlaneDeparture} /> Departure</label>
                                    <input type="text" value={displayFrom} onChange={(e) => handleAirportSearch(e, 'departure')} placeholder="Mumbai (BOM)" required />
                                    {departureResults.length > 0 && (<div className="autocomplete-suggestions">{departureResults.map(airport => (<div key={airport.airportCode} className="suggestion-item" onClick={() => handleDepartureSelect(airport)}><strong>{getCleanCityName(airport)} ({airport.airportCode})</strong><small> {airport.airportName}</small><small>{airport.countryName}</small></div>))}</div>)}
                                    {errors.departure && <div className="validation-error">{errors.departure}</div>}
                                </div>
                            </div>
                            <div className="col-3">
                                <div className="form-group">
                                    <label><FontAwesomeIcon icon={faPlaneArrival} /> Arrival</label>
                                    <input type="text" value={displayTo} onChange={(e) => handleAirportSearch(e, 'arrival')} placeholder="Dubai (DXB)" required />
                                    {arrivalResults.length > 0 && (<div className="autocomplete-suggestions">{arrivalResults.map(airport => (<div key={airport.airportCode} className="suggestion-item" onClick={() => handleArrivalSelect(airport)}><strong>{getCleanCityName(airport)} ({airport.airportCode})</strong><small> {airport.airportName}</small><small>{airport.countryName}</small></div>))}</div>)}
                                    {errors.arrival && <div className="validation-error">{errors.arrival}</div>}
                                </div>
                            </div>
                            <div className="col-2">
                                <div className="form-group">
                                    <label><FontAwesomeIcon icon={faCalendar} /> Start Date</label>
                                    <DatePicker selected={formData.startDate} onChange={handleStartDateChange} customInput={<CustomDateInput />} dateFormat="dd-MMM-yyyy" showYearDropdown showMonthDropdown dropdownMode="select" minDate={moment().add(1, 'days').toDate()} yearDropdownItemNumber={50} scrollableYearDropdown required />
                                </div>
                            </div>
                            <div className="col-2">
                                <div className="form-group">
                                    <label><FontAwesomeIcon icon={faCalendar} /> End Date</label>
                                    <DatePicker selected={formData.endDate} onChange={handleEndDateChange} customInput={<CustomDateInput />} dateFormat="dd-MMM-yyyy" showYearDropdown showMonthDropdown dropdownMode="select" minDate={formData.startDate} yearDropdownItemNumber={50} scrollableYearDropdown required />
                                    {totalTripNights > 0 && (
                                        <span className="nights-badge">{totalTripNights} {totalTripNights === 1 ? 'Night' : 'Nights'}</span>
                                    )}
                                </div>
                            </div>
                             <div className="col-2">
                                <div className="form-group passenger-selector" ref={passengerRef}>
                                    <label><FontAwesomeIcon icon={faUsers} /> Passengers</label>
                                    <div className="passenger-display" onClick={() => setPassengerDropdownOpen(!isPassengerDropdownOpen)}>{passengerDisplayText}</div>
                                    {isPassengerDropdownOpen && (
                                        <div className="passenger-dropdown">
                                            <div className="passenger-row"><span className="passenger-label">Adults</span><div className="passenger-controls"><button type="button" className="control-btn" onClick={() => handlePassengerChange('numAdults', -1)}><FontAwesomeIcon icon={faMinus} /></button><span>{formData.numAdults}</span><button type="button" className="control-btn" onClick={() => handlePassengerChange('numAdults', 1)}><FontAwesomeIcon icon={faPlus} /></button></div></div>
                                            <div className="passenger-row"><span className="passenger-label">Kids</span><div className="passenger-controls"><button type="button" className="control-btn" onClick={() => handlePassengerChange('numChildren', -1)}><FontAwesomeIcon icon={faMinus} /></button><span>{formData.numChildren}</span><button type="button" className="control-btn" onClick={() => handlePassengerChange('numChildren', 1)}><FontAwesomeIcon icon={faPlus} /></button></div></div>
                                            <div className="passenger-row"><span className="passenger-label">Infants</span><div className="passenger-controls"><button type="button" className="control-btn" onClick={() => handlePassengerChange('numInfants', -1)}><FontAwesomeIcon icon={faMinus} /></button><span>{formData.numInfants}</span><button type="button" className="control-btn" onClick={() => handlePassengerChange('numInfants', 1)}><FontAwesomeIcon icon={faPlus} /></button></div></div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="row">
                           
                        </div>

                        {/* Rest of the form remains unchanged */}
                        {formData.country && (
                            <>   <SuggestedCities country={formData.country} onAddCity={addCityFromSuggestion} remainingNights={remainingNights} itineraryCities={cities} />
                            <div className="form-group-full"><div className="label-with-info"><label>Your Itinerary Cities</label>{totalTripNights > 0 && (<span className={`nights-info ${areNightsBalanced ? 'balanced' : 'unbalanced'}`}>{areNightsBalanced ? `✓ ${allocatedNights}/${totalTripNights} Nights Allocated` : <><FontAwesomeIcon icon={faExclamationCircle} /> {allocatedNights} of {totalTripNights} Nights Allocated</>}</span>)}</div>
                           <div className="generate-city-card-grid">
              {cities.map((city, index) => (
                <CityCard key={index} city={city} onRemove={() => removeCity(index)} onNightsChange={(delta) => handleNightsChange(index, delta)} canAddNight={remainingNights > 0} />
              ))}
              <div className={`generate-add-city-card ${remainingNights <= 0 ? 'disabled' : ''}`} onClick={() => remainingNights > 0 && setCityModalOpen(true)}>
                <FontAwesomeIcon icon={faPlus} /> Add City
              </div>
            </div>
                            {cities.length > 0 && totalTripNights > 0 && (
              <div className="nights-helper-text-container">
                {areNightsBalanced ? ( <p className="nights-helper-text success"><FontAwesomeIcon icon={faCheckCircle} /> Perfect! All {totalTripNights} nights have been allocated.</p> ) : remainingNights > 0 ? ( <p className="nights-helper-text info"><FontAwesomeIcon icon={faInfoCircle} /> You have <strong>{remainingNights} {remainingNights === 1 ? 'night' : 'nights'}</strong> remaining to allocate.</p> ) : ( <p className="nights-helper-text error"><FontAwesomeIcon icon={faExclamationCircle} /> You have over-allocated by <strong>{-remainingNights} {(-remainingNights) === 1 ? 'night' : 'nights'}</strong>. Please reduce nights from a city.</p> )}
              </div>
            )}
            
            {errors.cities && <div className="validation-error">{errors.cities}</div>}
            {errors.nights && <div className="validation-error">{errors.nights}</div>}
          </div>
        </>
      )}
                        
                        <div className="advanced-options-toggle"><button type="button" onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}>Advanced Options <FontAwesomeIcon icon={showAdvancedOptions ? faChevronUp : faChevronDown} /></button></div>
                        <div className={`advanced-options-container ${showAdvancedOptions ? 'show' : ''}`}><div className="row"><div className="col-3"><div className="form-group"><label><FontAwesomeIcon icon={faBed} /> Hotel Category</label><select name="hotelCategory" value={formData.hotelCategory} onChange={handleInputChange}><option>3 Star</option><option>4 Star</option><option>5 Star</option></select></div></div><div className="col-3"><div className="form-group"><label><FontAwesomeIcon icon={faUtensils} /> Meal Type</label><select name="mealType" value={formData.mealType} onChange={handleInputChange}><option value="CP">Breakfast Only</option><option value="MAP">Breakfast & Dinner</option><option value="AP">All Meals</option></select></div></div><div className="col-3"><div className="form-group"><label><FontAwesomeIcon icon={faBed} /> No. of Rooms</label><input type="number" name="numRooms" value={formData.numRooms} onChange={handleInputChange} min="1" /></div></div></div></div>
                        <div className="form-actions">
                            <button type="button" className="generate-btn" onClick={handleGenerateClick} disabled={!areNightsBalanced}>✨ Generate Itinerary</button>
                        </div>
                    </form>
                )}
            </div>
            <Modal isOpen={isCityModalOpen} onClose={() => setCityModalOpen(false)} customClassName="add-city-modal" ><h3 className="modal-title">Add a City</h3><div className="modal-form-group"><label>City Name</label><input type="text" placeholder="e.g., Paris" value={newCityName} onChange={handleCitySearch} autoComplete="off" />{citySuggestions.length > 0 && (<div className="modal-autocomplete-suggestions">{citySuggestions.map((city, index) => (<div key={index} className="suggestion-item" onClick={() => handleCitySelect(city)}><strong>{city}</strong><small>City in {formData.country}</small></div>))}</div>)}</div><div className="modal-form-group"><label>Number of Nights</label><input type="number" placeholder="e.g., 3" value={newCityNights} onChange={e => setNewCityNights(e.target.value)} min="1" /></div><button type="button" className="modal-add-btn" onClick={handleAddCity} disabled={!newCityName.trim() || !newCityNights || allocatedNights + parseInt(newCityNights || 0) > totalTripNights}>Add City</button></Modal>
        </>
    );
};

export default SupplierAIGeneratorForm;